import React, { useState, useEffect, useRef, useContext, useCallback, useMemo } from 'react';
import cloneDeep from 'lodash/cloneDeep';
import Picker from 'react-month-picker';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  FormControlLabel,
  FormControl,
  FormLabel,
  Grid,
  InputAdornment,
  List,
  ListItem,
  ListItemText,
  Paper,
  Radio,
  RadioGroup,
  Stack,
  Switch,
  Tooltip,
  Typography,
  TextField,
  createFilterOptions,
} from '@mui/material';
import { styled } from '@mui/system';
import { format, parse, startOfMonth, endOfMonth } from 'date-fns';

import DataContext from '../../../Contexts/DataContext';
import {
  getMonthsBetweenDates,
  getAccountDetails,
  getNextExcelColumn,
  formatBackendDate,
  getMonthYearFromBackendDate,
} from '../../../Helpers/utils';
import {
  currencyFormatter,
  getRowId,
  referencePeriodTotal,
  referencePeriodMonth,
} from '../../../Helpers/aggrid';
import {
  calculateMasterStatChangeAllMonths,
  getChildStatDrivers,
  getDrivenAccountsByStatDriver,
  isMasterStatDriver,
} from '../../../Helpers/masterStatCalculations';
import { getChangeAuditTrail, addCommentToChange, getAccountsByGlKeys } from '../../../Helpers/dataApi';
import { OPERATORS, MASTER_STAT_PREFIX } from '../../../Helpers/constants';
import useCookie from '../../../Helpers/cookies';
import EditSuccessAlert from './EditSuccessAlert';
import AuditTrailDisplay from './AuditTrailDisplay';
import CommentInput from './CommentInput';
import ContextualTooltip from '../../../Components/Knowledgebase/ContextualTooltip';

// Icons
import IconButton from '@mui/material/IconButton';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import DeleteForeverOutlinedIcon from '@mui/icons-material/DeleteForeverOutlined';
import ClearIcon from '@mui/icons-material/Clear';

import { AgGridReact } from 'ag-grid-react';

// Test Data
import {
  basicChange,
  referenceAccountDominant, 
  statDriver 
} from './testChanges';


const defaultState = {
  accountsOptions: [], // Accounts dropdown options
  accountsSelected: [], // Accounts selected by Accounts dropdown
  changeDetails: {
    change_label: '',
    date_range: '',
    driven_accounts: [],
    function_operator: '',
    id: '',
    is_dominant_change: false,
    is_stat_driver: false,
    operator_value: '',
    parent_change: '',
    ref_account_formula: '',
    reference_accounts: [],
    selected_accounts: [],
    user_creator: '',
    user_note: '',
    user_timestamp: '',
  },
  dateRangeValue: '',
  departmentsOptions: [], // Departments dropdown options
  departmentsSelected: [], // Departments selected by dropdown
  entitiesOptions: [], // Entities dropdown options
  entitiesSelected: [], // Entities selected 
  errors: {
    invalidGlKeys: [], // Array of invalid GL key objects with descriptive information
    glKeyValidationError: '', // Error message for invalid GL keys
  },
  maxDate: endOfMonth(new Date()),
  minDate: startOfMonth(new Date()),
  monthRangeValue: {
    from: {
      year: new Date().getFullYear(),
      month: new Date().getMonth() + 1,
    },
    to: {
      year: new Date().getFullYear(),
      month: new Date().getMonth() + 1,
    }
  },
  monthPickerRange: {
    min: {
      year: new Date().getFullYear(),
      month: new Date().getMonth() + 1,
    },
    max: {
      year: new Date().getFullYear(),
      month: new Date().getMonth() + 1,
    }
  },
  parentChangeOptions: [],
  reference_accountsOptions: [],
  referenceAccountSelected: null,
  referenceGridData: false,
  referenceDepartmentsOptions: [],
  referenceDepartmentSelected: null,
  referenceEntitiesOptions: [],
  referenceEntitySelected: null,
};

const pickerLang = {
  months: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
  from: 'From', to: 'To',
}

// ---------------------------------------------------------------------------
// Module-level pure helpers
// Extracted here so they can be unit-tested independently of the component.
// ---------------------------------------------------------------------------

/**
 * Converts the raw masterStats array from context into selectable option objects
 * for the accounts Autocomplete. Extracted to avoid duplicating this mapping in
 * _renderOptions, clearChangeForm, and the changeToEdit useEffect.
 *
 * @param {Array} masterStats  - context.activeProjectAttributes.masterStats
 * @param {Array} entities     - context.activeProjectAttributes.entities
 * @param {Array} departments  - context.activeProjectAttributes.departments
 * @returns {Array} Option objects with isMasterStat: true
 */
export const buildMasterStatOptions = (masterStats = [], entities = [], departments = []) => {
  return masterStats.map(ms => {
    const entity = entities.find(e => e.entity_id === ms.entity_id);
    const department = departments.find(d =>
      d.departmentId === ms.department_id && d.entityId === ms.entity_id
    );
    return {
      departmentId: ms.department_id,
      entityId: ms.entity_id,
      id: ms.gl_key,
      label: `\u2295 ${ms.master_stat_name}`,
      parentAccount: 'Master Stats',
      isMasterStat: true,
      master_stat_name: ms.master_stat_name,
      child_stats: ms.child_stats,
      entity_name: entity?.entity_name || '',
      department_name: department?.departmentName || '',
    };
  });
};

/**
 * Classifies a list of resolved GL keys into three buckets used by
 * _handlePreviewChange and the affectedStatDriversPreview memo.
 * Centralises the logic that was previously duplicated in both places.
 *
 * Each regular stat driver entry has its drivenAccounts scoped to the driver's
 * own entity-department only — a wRVU in dept 1000 only drives accounts in
 * dept 1000, never dept 1100, even if both were selected.
 *
 * @param {string[]}  glKeys              - Resolved GL keys (may include MASTER- prefixed keys)
 * @param {Array}     masterStats         - context.activeProjectAttributes.masterStats
 * @param {Array}     accountsFullDetails - context.activeProjectAttributes.accountsMetadata
 * @returns {{
 *   affectedMasterStats: Array,
 *   affectedByChildStatChanges: Array,
 *   regularStatDrivers: Array,
 * }}
 */
export const classifySelectedAccounts = (glKeys, masterStats = [], accountsFullDetails = []) => {
  // Pre-index rows by GL key for O(1) lookups inside the loops below
  const rowsByGlKey = new Map(accountsFullDetails.map(r => [r.gl_key, r]));

  const affectedMasterStats = [];
  const affectedByChildStatChanges = [];
  const regularStatDrivers = [];

  // Track what we've already added to avoid duplicates
  const addedMasterStatKeys = new Set();
  const addedDriverKeys = new Set();

  glKeys.forEach(glKey => {
    if (glKey.startsWith(MASTER_STAT_PREFIX)) {
      // Master Stat
      const ms = masterStats.find(m => m.gl_key === glKey);
      if (ms && !addedMasterStatKeys.has(glKey)) {
        affectedMasterStats.push(ms);
        addedMasterStatKeys.add(glKey);
      }
    } else {
      // Regular account
      // 1. Is it a child of any Master Stat?
      const parentMasterStats = masterStats.filter(
        ms => ms.child_stats?.includes(glKey)
      );
      if (parentMasterStats.length > 0) {
        affectedByChildStatChanges.push({ childGlKey: glKey, parentMasterStats });
      }

      // 2. Is it a regular stat driver?
      const detail = rowsByGlKey.get(glKey);
      if (!detail) return;

      const isStatDriver =
        detail.acct_type === 'Stat' &&
        accountsFullDetails.some(a => a.driver === detail.account_name);

      if (isStatDriver && !addedDriverKeys.has(glKey)) {
        // Scope driven accounts to the driver's own entity-department only.
        // A stat driver in dept 1000 only drives accounts in dept 1000 — it does
        // NOT drive accounts in dept 1100 even if the user selected wRVUs in both
        // departments. Storing the full cross-department list for every driver
        // causes O(D²) payload growth when the same stat driver name exists across
        // many departments (e.g. 21 wRVU GL keys × 1,218 driven accounts = 25,578
        // entries vs. the correct 21 × 58 = 1,218).
        const driverEntityDept = `${detail.entity_id}-${detail.department_id}`;
        const drivenAccounts = accountsFullDetails.filter(a => {
          if (a.driver !== detail.account_name) return false;
          // Only include driven accounts in the same entity-department as this driver
          return `${a.entity_id}-${a.department_id}` === driverEntityDept;
        }).map(a => ({
          entity_id: a.entity_id,
          department_id: a.department_id,
          account_id: a.account_id,
          account_name: a.account_name,
          gl_key: a.gl_key,
        }));

        if (drivenAccounts.length > 0) {
          regularStatDrivers.push({
            type: 'regular_stat',
            gl_key: glKey,
            name: detail.account_name,
            drivenAccounts,
          });
          addedDriverKeys.add(glKey);
        }
      }
    }
  });

  return { affectedMasterStats, affectedByChildStatChanges, regularStatDrivers };
};const GroupHeader = styled('div')(({ theme }) => ({
  backgroundColor: 'white',
  // theme.palette.mode === 'light'
  //   ? lighten(theme.palette.primary.light, 0.85)
  //   : darken(theme.palette.primary.main, 0.8),
  // color: theme.palette.primary.main,
  cursor: 'pointer',
  fontWeight: 'bold',
  position: 'sticky',
  padding: '4px 10px',
  top: '-8px',
  '&:hover': {
    backgroundColor: '#b9dcff',
  }
}));

const GroupItems = styled('ul')({
  padding: 0,
});

const SelectAll = styled('li')({
  fontWeight: 'bold',
  textDecoration: 'underline'
});

// Number of alert list items to show before collapsing — lifted to module scope
// so it's not re-created on every render.
const ALERT_LIST_PREVIEW = 5;

const ChangeDetails = (props) => {
  const { 
    changeToEdit, 
    onEntitiesChange, 
    onDepartmentSelected, 
    previewChange, 
    cancelChange,
    onCalculatingStart,
    onCalculatingFailed,
  } = props;
  const context = useContext(DataContext);
  const [activeUser] = useCookie('activeUser', false);
  const monthPickerRef = useRef();
  const gridRef = useRef();
  
  // State management - breaking down the large state object into logical groups
  const [accountsOptions, setAccountsOptions] = useState([]);
  const [accountsSelected, setAccountsSelected] = useState([]);
  const [changeDetailsState, setChangeDetailsState] = useState(defaultState.changeDetails);
  const [dateRangeValue, setDateRangeValue] = useState(defaultState.dateRangeValue);
  const [departmentsOptions, setDepartmentsOptions] = useState([]);
  const [departmentsSelected, setDepartmentsSelected] = useState([]);
  const [entitiesOptions, setEntitiesOptions] = useState([]);
  const [entitiesSelected, setEntitiesSelected] = useState([]);
  const [errors, setErrors] = useState({});
  const [maxDate, setMaxDate] = useState(defaultState.maxDate);
  const [minDate, setMinDate] = useState(defaultState.minDate);
  const [monthRangeValue, setMonthRangeValue] = useState(defaultState.monthRangeValue);
  const [monthPickerRange, setMonthPickerRange] = useState(defaultState.monthPickerRange);
  const [parentChangeOptions, setParentChangeOptions] = useState([]);
  const [reference_accountsOptions, setReference_accountsOptions] = useState([]);
  const [referenceAccountSelected, setReferenceAccountSelected] = useState(null);
  const [referenceGridData, setReferenceGridData] = useState(false);
  const [referenceDepartmentsOptions, setReferenceDepartmentsOptions] = useState([]);
  const [referenceDepartmentSelected, setReferenceDepartmentSelected] = useState(null);
  const [referenceEntitiesOptions, setReferenceEntitiesOptions] = useState([]);
  const [referenceEntitySelected, setReferenceEntitySelected] = useState(null);
  const [referenceColumns, setReferenceColumns] = useState([]);
  const [selectedMasterStats, setSelectedMasterStats] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false); // True while change is being saved/polled
  const [selectedChildStatsWithParents, setSelectedChildStatsWithParents] = useState([]);
  
  // Audit trail state management
  const [auditTrail, setAuditTrail] = useState([]);
  const [showEditSuccess, setShowEditSuccess] = useState(false);
  const [showCommentInput, setShowCommentInput] = useState(false);

  // Alert list collapse state — start collapsed so long lists don't bury the form
  const [statDriverAlertExpanded, setStatDriverAlertExpanded] = useState(false);
  const [childStatAlertExpanded, setChildStatAlertExpanded] = useState(false);

  /**
   * Calculate affected stat drivers based on selected accounts.
   * Uses classifySelectedAccounts so the logic stays in sync with _handlePreviewChange.
   * @returns {Array} Array of stat driver info objects
   */
  const affectedStatDriversPreview = useMemo(() => {
    const { activeProjectAttributes } = context;
    if (!activeProjectAttributes || !accountsSelected || accountsSelected.length === 0) {
      return [];
    }

    const glKeys = accountsSelected.map(
      entry => entry.isMasterStat ? entry.id : entry.gl_key
    ).filter(Boolean);

    const { affectedMasterStats, affectedByChildStatChanges, regularStatDrivers } =
      classifySelectedAccounts(
        glKeys,
        activeProjectAttributes.masterStats || [],
        activeProjectAttributes.accountsMetadata || [],
      );

    const affectedDrivers = [];

    // Master Stats that are themselves drivers
    affectedMasterStats.forEach(masterStat => {
      if (isMasterStatDriver(masterStat, activeProjectAttributes.accountsMetadata)) {
        const masterStatDept = masterStat.gl_key ? masterStat.gl_key.split('-').slice(0, 2).join('-') : null;
        const drivenAccounts = getDrivenAccountsByStatDriver(
          masterStat.gl_key,
          activeProjectAttributes.accountsMetadata
        ).filter(a =>
          !masterStatDept || `${a.entity_id}-${a.department_id}` === masterStatDept
        );
        if (drivenAccounts.length > 0) {
          affectedDrivers.push({
            type: 'master_stat',
            gl_key: masterStat.gl_key,
            name: masterStat.master_stat_name,
            drivenAccounts,
          });
        }
      }
    });

    // Child stats that are drivers
    affectedByChildStatChanges.forEach(item => {
      const childDrivers = item.parentMasterStats.flatMap(ms =>
        getChildStatDrivers(ms, activeProjectAttributes.accountsMetadata)
      );
      if (childDrivers.includes(item.childGlKey)) {
        const account = (activeProjectAttributes.accountsMetadata || []).find(
          acc => acc.gl_key === item.childGlKey
        );
        const childDept = item.childGlKey ? item.childGlKey.split('-').slice(0, 2).join('-') : null;
        const drivenAccounts = getDrivenAccountsByStatDriver(
          item.childGlKey,
          activeProjectAttributes.accountsMetadata
        ).filter(a =>
          !childDept || `${a.entity_id}-${a.department_id}` === childDept
        );
        if (drivenAccounts.length > 0) {
          affectedDrivers.push({
            type: 'child_stat',
            gl_key: item.childGlKey,
            name: account?.account_name || item.childGlKey,
            drivenAccounts,
          });
        }
      }
    });

    // Regular stat drivers
    affectedDrivers.push(...regularStatDrivers);

    return affectedDrivers;
  }, [accountsSelected, context]);

  /**
   * Get unique Master Stats from child stats with parents
   * @returns {Array} Array of unique Master Stat objects
   */
  const getUniqueMasterStatsFromChildren = useMemo(() => {
    if (!selectedChildStatsWithParents || selectedChildStatsWithParents.length === 0) {
      return [];
    }
    
    const uniqueMasterStatsMap = new Map();
    selectedChildStatsWithParents.forEach(item => {
      item.parentMasterStats.forEach(ms => {
        if (!uniqueMasterStatsMap.has(ms.gl_key)) {
          uniqueMasterStatsMap.set(ms.gl_key, ms);
        }
      });
    });
    
    return Array.from(uniqueMasterStatsMap.values());
  }, [selectedChildStatsWithParents]);

  const clearContextValues = () => {
    context.clearContextValues();
  };

  /**
   * Fetch audit trail for a change
   * @param {number} changeId - ID of the change
   */
  const fetchAuditTrail = useCallback(async (changeId) => {
    try {
      const accessToken = activeUser?.accessToken;
      if (!accessToken) {
        console.error('No access token available');
        return;
      }
      const response = await getChangeAuditTrail(changeId, accessToken);
      
      // getChangeAuditTrail returns an Axios response or an error object.
      // Explicitly check for a successful 200 response before using response.data.
      if (response && response.status === 200 && response.data) {
        setAuditTrail(response.data);
      } else {
        console.error('Failed to fetch audit trail: unexpected response', response);
        setAuditTrail([]);
      }
    } catch (error) {
      console.error('Failed to fetch audit trail:', error);
      // Optionally show error toast to user
      setAuditTrail([]);
    }
  }, [activeUser?.accessToken]);

  /**
   * Handle comment submission
   * @param {string} commentText - The comment text to submit
   */
  const handleCommentSubmit = async (commentText) => {
    try {
      const accessToken = activeUser?.accessToken;
      if (!accessToken) {
        console.error('No access token available');
        return false;
      }
      const response = await addCommentToChange(changeDetailsState.id, commentText, accessToken);
      
      // Check response status before proceeding
      if (!response || (response.status !== 200 && response.status !== 201)) {
        console.error('Failed to add comment: unexpected response status', response?.status);
        window.alert('Failed to add comment. Please try again.');
        return false;
      }
      
      // Invalidate cache to force refetch of audit trail with new comment
      if (context?.invalidateAuditTrailCache) {
        context.invalidateAuditTrailCache(changeDetailsState.id, 'change');
      }
      
      await fetchAuditTrail(changeDetailsState.id);
      setShowCommentInput(false);
      return true;
    } catch (error) {
      console.error('Failed to add comment:', error);
      window.alert('Failed to add comment. Please try again.');
      return false;
    }
  };

  /**
   * @param {Object} params Parameters for an AG Grid row
   * @returns Delete icon tied to a row
   */
  const deleteIconRenderer = useCallback((params) => {
    const handleClick = () => {
      if(params.onIconClick){
        params.onIconClick(params.data);
      }
    }
    return <IconButton onClick={() => handleClick()}>
              <DeleteForeverOutlinedIcon color={'error'} />
            </IconButton>
  }, []);

  /**
   * Delete a reference account from the Reference Account AG Grid
   * @param {Object} data Row data for the reference account to be deleted
   */
  const handleReferenceAccountDelete = useCallback((data) => {
    setChangeDetailsState(prev => ({
      ...prev,
      reference_accounts: prev.reference_accounts ? prev.reference_accounts.filter(acc => acc.rowLetter !== data.variable) : [],
    }));
    setErrors(prev => ({
      ...prev,
      refAccountError: '',
    }));
    setReferenceGridData(prev =>
      Array.isArray(prev) ? prev.filter(row => row.glKey !== data.glKey) : []
    );
  }, []);

  /**
   * Helper function to match a row against a GL key, supporting multiple formats
   * @param {Object} row The row to check
   * @param {string} glKey The GL key to match against
   * @returns {boolean} True if the row matches the GL key
   */
  const matchesGlKey = useCallback((row, glKey) => {
    // Try direct glKey match first
    if (row.glKey === glKey) return true;
    
    // Try constructing GL key from component parts
    const constructedKey = row.entityId && row.departmentId && row.accountId 
      ? `${row.entityId}-${row.departmentId}-${row.accountId}`
      : null;
    
    return constructedKey === glKey;
  }, []);

  /**
   * Build the rowData object for the Reference Account AG Grid 
   * @param {Object} changeDetailsData Details of the current change
   */
  /**
   * Builds a normalised row object for a reference account grid entry.
   *
   * The row's `basePeriodDates` is guaranteed to be a plain object keyed in
   * 'yyyy-MM-dd' format, which is what `referencePeriodMonth` and
   * `referencePeriodTotal` (aggrid.jsx) expect.  We no longer rely on
   * `buildAgGridTable()` (which hits the no-dates metadata endpoint and
   * therefore returns rows with `basePeriodDates: undefined`).  Instead,
   * `accountDates` is the raw dates object fetched via `getAccountsByGlKeys`
   * which always includes the full monthly values.
   *
   * @param {string} glKey - GL key for this row
   * @param {string} rowLetter - Excel-column letter (B, C, …)
   * @param {Object} accountDates - Raw dates object from the API, keyed as
   *   'yyyy-MM-dd' (e.g. { "2024-01-01": 50000 })
   * @param {Object} extraFields - Additional fields to merge (account name, etc.)
   */
  const buildNormalisedRefRow = useCallback((glKey, rowLetter, accountDates, extraFields = {}) => {
    // Normalise: ensure every key is 'yyyy-MM-dd' regardless of source format.
    // The API currently returns ISO keys, but guard against legacy 'MMM yyyy' keys.
    const normalisedDates = {};
    Object.entries(accountDates || {}).forEach(([k, v]) => {
      if (/^\d{4}-\d{2}-\d{2}$/.test(k)) {
        normalisedDates[k] = v;
      } else {
        // Attempt to convert 'MMM yyyy' → 'yyyy-MM-dd'
        try {
          normalisedDates[format(parse(k, 'MMM yyyy', new Date()), 'yyyy-MM-dd')] = v;
        } catch (_) {
          normalisedDates[k] = v; // leave as-is if unparseable
        }
      }
    });

    return {
      ...extraFields,
      id: glKey,
      glKey,
      variable: rowLetter,
      basePeriodDates: normalisedDates,
      changes: [],
    };
  }, []);

  /**
   * Builds the reference account grid columns and row data, then sets state.
   *
   * Row data is now sourced via `getAccountsByGlKeys` (the bulk GL key endpoint
   * that includes full monthly dates) instead of `buildAgGridTable()` (which hits
   * the metadata-only endpoint and returns rows with no dates).
   *
   * @param {Object} changeDetailsData - Current change details (includes reference_accounts and date_range)
   */
  const buildReferenceAgGridTable = useCallback(async (changeDetailsData) => {
    const accessToken = activeUser?.accessToken;
    const projectId = context.activeProjectInfo?.id;

    const columns = [
      {
        cellRenderer: deleteIconRenderer,
        cellRendererParams: {
          onIconClick: handleReferenceAccountDelete,
        },
        field: 'delete',
        headerName: ' ',
      },
      {
        field: 'variable',
      },
      {
        field: 'accountName'
      }
    ];

    const dateRange = getMonthsBetweenDates(changeDetailsData.date_range[0], changeDetailsData.date_range[1]);

    dateRange.forEach((month) => {
      columns.push({
        field: month,
        headerName: month,
        type: 'month',
        valueFormatter: currencyFormatter,
        valueGetter: (params) => referencePeriodMonth(params),
      });
    });

    columns.push({
      cellStyle: { fontWeight: "bold" },
      field: 'total',
      headerName: 'Period Total',
      pinned: 'right',
      valueFormatter: currencyFormatter,
      valueGetter: (params) => referencePeriodTotal(params, dateRange),
    });

    const reference_accounts = cloneDeep(changeDetailsData.reference_accounts);

    if (!reference_accounts || reference_accounts.length === 0) {
      setChangeDetailsState(changeDetailsData);
      setErrors(prev => ({ ...prev, refAccountError: '' }));
      setReferenceColumns(columns);
      setReferenceGridData([]);
      return;
    }

    // Fetch full account data (including dates) for all reference account GL keys.
    // This replaces the old buildAgGridTable() call which used the no-dates metadata endpoint.
    const glKeys = reference_accounts.map(ra => ra.glKey).filter(Boolean);
    let accountDatesMap = {};

    if (accessToken && projectId) {
      try {
        const response = await getAccountsByGlKeys(projectId, glKeys, accessToken);
        if (response?.status === 200 && response?.data?.accounts) {
          response.data.accounts.forEach(account => {
            accountDatesMap[account.gl_key] = account.dates || {};
          });
        }
      } catch (err) {
        console.error('[buildReferenceAgGridTable] Failed to fetch account dates:', err);
      }
    }

    const referenceGridDataLocal = [];
    const accountsFullDetails = context.activeProjectAttributes?.accountsMetadata || [];

    reference_accounts.forEach((referenceAccount) => {
      const glKey = referenceAccount.glKey;
      const accountDates = accountDatesMap[glKey] || {};

      // Look up display metadata (account name) from accountsMetadata.
      // This is metadata-only so it's always available; we just use it for display.
      const metaAccount = accountsFullDetails.find(a => a.gl_key === glKey);
      const accountName = metaAccount
        ? `${metaAccount.account_id} - ${metaAccount.account_name}`
        : glKey;

      referenceGridDataLocal.push(
        buildNormalisedRefRow(glKey, referenceAccount.rowLetter, accountDates, { accountName })
      );
    });

    setChangeDetailsState(changeDetailsData);
    setErrors(prev => ({ ...prev, refAccountError: '' }));
    setReferenceColumns(columns);
    setReferenceGridData(referenceGridDataLocal);
  }, [activeUser?.accessToken, context.activeProjectInfo?.id, context.activeProjectAttributes?.accountsMetadata, deleteIconRenderer, handleReferenceAccountDelete, buildNormalisedRefRow]);

  const _renderOptions = useCallback(() => {
    const {
      activeProjectInfo,
      activeProjectAttributes,
    } = context;
  
    if (activeProjectInfo) {
      const startMonth = activeProjectInfo.start_date;
      const lastMonth = activeProjectInfo.end_date;
  
      const parentChangeOptionsData = activeProjectAttributes.parent_changes.map((pc) => pc.label);

      // Use the new helper function to properly parse backend dates
      const maxDateRangeStart = getMonthYearFromBackendDate(activeProjectInfo.start_date);
      const maxDateRangeEnd = getMonthYearFromBackendDate(activeProjectInfo.end_date);

      const entities = activeProjectAttributes.entities;
      const departments = activeProjectAttributes.departments;
      const accounts = activeProjectAttributes.accounts;
      
      // Build combined account list with Master Stats
      const masterStatAccounts = buildMasterStatOptions(
        activeProjectAttributes.masterStats,
        entities,
        departments,
      );
      const allSelectableAccounts = [...(accounts || []), ...masterStatAccounts];

      setChangeDetailsState(prev => ({
        ...prev,
        date_range: [startMonth, lastMonth],
      }));
      setEntitiesOptions(entities);
      setDepartmentsOptions(departments);
      setAccountsOptions(allSelectableAccounts);
      setMonthRangeValue({
        from: maxDateRangeStart,
        to: maxDateRangeEnd,
      });
      setMonthPickerRange({
        min: maxDateRangeStart,
        max: maxDateRangeEnd,
      });
      setParentChangeOptions(parentChangeOptionsData);
      setReferenceEntitiesOptions(entities);
      setReferenceDepartmentsOptions(departments);
      setReference_accountsOptions(accounts);
    }
  }, [
    context.activeProjectInfo,
    context.activeProjectAttributes.entities,
    context.activeProjectAttributes.departments,
    context.activeProjectAttributes.accounts,
    context.activeProjectAttributes.masterStats,
    context.activeProjectAttributes.parent_changes
  ]);

  /**
   * @implements Resets the Change Details form fields
   */
  const clearChangeForm = useCallback(() => {
    const { activeProjectInfo, activeProjectAttributes } = context;

    const startMonth = activeProjectInfo.start_date;
    const lastMonth = activeProjectInfo.end_date;

    // Parse dates properly to avoid timezone issues
    const minRangeDate = formatBackendDate(startMonth);
    const maxRangeDate = formatBackendDate(lastMonth);

    const maxDateRangeStart = getMonthYearFromBackendDate(activeProjectInfo.start_date);
    const maxDateRangeEnd = getMonthYearFromBackendDate(activeProjectInfo.end_date);

    // Reset form selections without clearing context data
    setAccountsOptions([]);
    setAccountsSelected([]);
    setSelectedMasterStats([]);
    setSelectedChildStatsWithParents([]);
    setChangeDetailsState({
      change_label: '',
      date_range: [minRangeDate, maxRangeDate],
      driven_accounts: [],
      function_operator: '',
      id: '',
      is_dominant_change: false,
      is_stat_driver: false,
      operator_value: '',
      parent_change: '',
      ref_account_formula: '',
      reference_accounts: [],
      selected_accounts: [],
      user_creator: '',
      user_note: '',
      user_timestamp: '',
    });
    setDepartmentsOptions([]);
    setDepartmentsSelected([]);
    // Preserve entities from context
    setEntitiesOptions(activeProjectAttributes.entities || []);
    setEntitiesSelected([]);
    setReference_accountsOptions([]);
    setReferenceAccountSelected(null);
    setReferenceEntitiesOptions(activeProjectAttributes.entities || []);
    setReferenceDepartmentsOptions([]);
    setReferenceDepartmentSelected([]);
    setMonthRangeValue({
      from: maxDateRangeStart,
      to: maxDateRangeEnd,
    });
    setMonthPickerRange({
      min: maxDateRangeStart,
      max: maxDateRangeEnd,
    });
    // Clear audit trail and success states
    setAuditTrail([]);
    setShowEditSuccess(false);
    setShowCommentInput(false);
  }, [context]);

  // useEffect equivalent to componentDidMount - runs once on mount
  useEffect(() => {
    _renderOptions();
  }, []); // Empty dependency array - only run on mount
  
  // useEffect to watch for context changes and update options
  // Only runs when the actual context data changes, not when local state changes
  useEffect(() => {
    _renderOptions();
  }, [_renderOptions]);

  // useEffect equivalent to componentDidUpdate for changeToEdit prop
  // Track previous value to detect transitions
  const prevChangeToEditRef = useRef();
  
  useEffect(() => {
    const prevChangeToEdit = prevChangeToEditRef.current;
    
    (async () => {
    if(changeToEdit){
      const { activeProjectAttributes } = context;

      // Reconstruct entity/department/account selections directly from accountsMetadata.
      // No GL key parsing or Cartesian product needed — filter the source of truth.
      const accountsFullDetails = activeProjectAttributes.accountsMetadata || [];
      const storedGlKeys = new Set(changeToEdit.selected_accounts || []);

      // Regular accounts: filter accountsMetadata to the stored GL keys
      const resolvedAccounts = accountsFullDetails.filter(a => storedGlKeys.has(a.gl_key));

      // Master Stats: pass through unchanged, using the shared helper
      const masterStatAccounts = (changeToEdit.selected_accounts || [])
        .filter(glKey => glKey.startsWith(MASTER_STAT_PREFIX))
        .map(glKey => {
          const ms = activeProjectAttributes.masterStats?.find(m => m.gl_key === glKey);
          if (!ms) return null;
          return buildMasterStatOptions([ms], activeProjectAttributes.entities, activeProjectAttributes.departments)[0];
        })
        .filter(Boolean);

      const selected_accounts = [...resolvedAccounts, ...masterStatAccounts];

      // Derive unique entities and departments from the resolved accounts
      const selectedEntitiesMap = new Map();
      const selectedDepartmentsMap = new Map();

      selected_accounts.forEach(entry => {
        const entityId = entry.isMasterStat ? entry.entityId : entry.entity_id;
        const deptId   = entry.isMasterStat ? entry.departmentId : entry.department_id;

        const entity = activeProjectAttributes.entities?.find(e =>
          String(e.entity_id) === String(entityId)
        );
        if (entity) selectedEntitiesMap.set(String(entity.entity_id), entity);

        const dept = activeProjectAttributes.departments?.find(d =>
          String(d.departmentId) === String(deptId) &&
          String(d.entityId) === String(entityId)
        );
        if (dept) selectedDepartmentsMap.set(`${dept.entityId}-${dept.departmentId}`, dept);
      });

      const selectedEntities   = Array.from(selectedEntitiesMap.values());
      const selectedDepartments = Array.from(selectedDepartmentsMap.values());

      // Parse dates from the change using the unified helper
      const startMonthYear = getMonthYearFromBackendDate(changeToEdit.date_range[0]);
      const endMonthYear = getMonthYearFromBackendDate(changeToEdit.date_range[1]);

      const dateRange = {
        from: startMonthYear,
        to: endMonthYear
      };

      // Update all state with populated data
      setEntitiesSelected(selectedEntities);
      setDepartmentsSelected(selectedDepartments);
      setAccountsSelected(selected_accounts);
      
      // Populate department options based on selected entities
      if (selectedEntities.length > 0) {
        const departmentOptionsForEntities = activeProjectAttributes.departments?.filter(dept =>
          selectedEntities.some(entity => entity.entity_id === dept.entityId)
        ) || [];
        setDepartmentsOptions(departmentOptionsForEntities);
      }
      
      // Populate account options based on selected entities and departments
      if (selectedEntities.length > 0 && selectedDepartments.length > 0) {
        const allSelectableAccounts = [
          ...(activeProjectAttributes.accounts || []),
          ...buildMasterStatOptions(
            activeProjectAttributes.masterStats,
            activeProjectAttributes.entities,
            activeProjectAttributes.departments,
          ),
        ];
        setAccountsOptions(allSelectableAccounts);
      }
      
      setChangeDetailsState({
        change_label: changeToEdit.change_label,
        date_range: changeToEdit.date_range,
        driven_accounts: changeToEdit.driven_accounts,
        function_operator: changeToEdit.function_operator,
        id: changeToEdit.id,
        is_dominant_change: changeToEdit.is_dominant_change,
        is_stat_driver: changeToEdit.is_stat_driver,
        operator_value: changeToEdit.operator_value,
        parent_change: changeToEdit.parent_change,
        ref_account_formula: changeToEdit.ref_account_formula,
        reference_accounts: changeToEdit.reference_accounts || [],
        selected_accounts: changeToEdit.selected_accounts,
        user_creator: changeToEdit.user_id,
        user_note: changeToEdit.user_note,
        user_timestamp: changeToEdit.user_timestamp,
      });
      setMonthRangeValue(dateRange);
      
      if(changeToEdit.function_operator === OPERATORS.REFERENCE){
        await buildReferenceAgGridTable(changeToEdit);
      }
    } else if (prevChangeToEdit) {
      // Only clear form when transitioning FROM having a value TO null
      // This prevents clearing on initial mount when changeToEdit is already null
      clearChangeForm();
    }
    
    // Update ref for next render
    prevChangeToEditRef.current = changeToEdit;
    })();
  }, [changeToEdit, buildReferenceAgGridTable, clearChangeForm]);

  // Fetch audit trail when editing existing change
  useEffect(() => {
    if (changeToEdit && changeToEdit.id) {
      fetchAuditTrail(changeToEdit.id);
    }
  }, [changeToEdit, fetchAuditTrail]);

  /**
   * @param {Number} id ID of an Entity
   * @returns Name of the Entity specified by the parameter
   */
  const getEntityName = (id) => {
    const { activeProject } = context;
    if(typeof id !== "undefined"){
      return activeProject.treeData.entities[id].entityName;
    }
    return id;
  };

  /**
   * Consumes an event from a form change. Assigns values based on the field changed.
   * @param {Event} e Event from a form field change
   */
  const handleChange = (e) => {
    const target = e.target.id ? e.target.id : e.target.name;
    const value = e.target.value;
    setChangeDetailsState(prev => ({
      ...prev,
      [target]: value,
    }));
  };

  const handleparent_change = (value) => {
    setChangeDetailsState(prev => ({
      ...prev,
      parent_change: value,
    }));
  };

  /**
   * Compute filtered accounts that can form valid GL keys with current entity/department selections
   * This ensures only valid accounts are shown in the dropdown
   */
  // ---------------------------------------------------------------------------
  // GL key resolution — single source of truth
  // ---------------------------------------------------------------------------

  /**
   * Memoized Set of all valid GL keys in the project.
   * Used by filteredAccountsOptions to decide which accounts to show in the
   * dropdown. NOT used for building selected_accounts — that goes through
   * resolveGlKeys instead.
   */
  const glKeyCache = useMemo(() => {
    const afd = context.activeProjectAttributes?.accountsMetadata;
    if (!afd) return new Set();
    const cache = new Set();
    afd.forEach(a => { if (a.gl_key) cache.add(String(a.gl_key)); });
    return cache;
  }, [context.activeProjectAttributes?.accountsMetadata]);

  /**
   * Resolve the full set of valid accountsMetadata entries that match the
   * user's current entity + department + account selections.
   *
   * Replaces the old Cartesian-product validateGlKeys approach. Instead of
   * constructing candidate GL keys and checking them against a cache, we filter
   * accountsMetadata directly — the source of truth already contains every
   * valid GL key with all metadata attached.
   *
   * Master Stats are passed through unchanged (they are not in accountsMetadata).
   *
   * @param {Array} selectedAccountOptions  - Raw option objects from the accounts autocomplete
   * @param {Array} selectedEntities        - Entity objects from entitiesSelected
   * @param {Array} selectedDepartments     - Department objects from departmentsSelected
   * @param {Array} accountsFullDetails     - context.activeProjectAttributes.accountsMetadata
   * @returns {{ resolved: Array, invalidGlKeys: Array }}
   *   resolved      — accountsMetadata entries (+ Master Stat pass-throughs) to store in accountsSelected
   *   invalidGlKeys — account options that exist somewhere in the project but not in this entity/dept combo
   */
  const resolveGlKeys = useCallback((
    selectedAccountOptions,
    selectedEntities,
    selectedDepartments,
    accountsFullDetails,
  ) => {
    // Empty dependency array is intentional — resolveGlKeys is a pure function that
    // closes over no component state or props. Adding deps would cause unnecessary
    // re-creation on every render without any benefit.
    const entityIds   = new Set(selectedEntities.map(e => String(e.entity_id)));
    const deptIds     = new Set(selectedDepartments.map(d => String(d.departmentId)));
    const accountIds  = new Set(
      selectedAccountOptions
        .filter(a => !a.isMasterStat)
        .map(a => String(a.account_id ?? a.id))  // handles both accountsMetadata and option-object shapes
    );

    // All account_ids that exist anywhere in the project (for invalid-key reporting)
    const allAccountIds = new Set(accountsFullDetails.map(a => String(a.account_id)));

    // 1. Resolve regular accounts directly from accountsMetadata
    const resolvedMap = new Map(); // gl_key → entry
    accountsFullDetails.forEach(entry => {
      if (
        entityIds.has(String(entry.entity_id)) &&
        deptIds.has(String(entry.department_id)) &&
        accountIds.has(String(entry.account_id))
      ) {
        resolvedMap.set(entry.gl_key, entry);
      }
    });

    // 2. Pass Master Stats through unchanged — they are virtual and not in accountsMetadata
    const masterStatPassthroughs = selectedAccountOptions.filter(a => a.isMasterStat);

    // 3. Identify invalid GL keys — account exists in the project but not in this combo
    //    (silently skip accounts that don't exist anywhere, e.g. all-zero rows never stored)
    const invalidGlKeys = [];
    selectedAccountOptions
      .filter(a => !a.isMasterStat)
      .forEach(account => {
        const accountIdStr = String(account.account_id ?? account.id);
        if (!allAccountIds.has(accountIdStr)) return; // doesn't exist anywhere — skip silently

        // Check if at least one valid GL key was resolved for this account
        const hasResolved = [...resolvedMap.values()].some(
          e => String(e.account_id) === accountIdStr
        );
        if (!hasResolved) {
          // Account exists in the project but not in the selected entity/dept combo
          const accountName = account.account_name || account.label || accountIdStr;
          selectedEntities.forEach(entity => {
            selectedDepartments
              .filter(d => String(d.entityId) === String(entity.entity_id))
              .forEach(dept => {
                invalidGlKeys.push({
                  glKey: `${entity.entity_id}-${dept.departmentId}-${accountIdStr}`,
                  accountName,
                  accountId: accountIdStr,
                  entityName: entity.entity_name,
                  entityId: entity.entity_id,
                  departmentName: dept.departmentName,
                  departmentId: dept.departmentId,
                });
              });
          });
        }
      });

    return {
      resolved: [...resolvedMap.values(), ...masterStatPassthroughs],
      invalidGlKeys,
    };
  }, []);

  const filteredAccountsOptions = useMemo(() => {
    if (accountsOptions.length === 0 || entitiesSelected.length === 0 || departmentsSelected.length === 0) {
      return accountsOptions;
    }

    if (glKeyCache.size === 0) {
      return accountsOptions;
    }

    return accountsOptions.filter((candidateAccount) => {
      // Special handling for Master Stats
      if (candidateAccount.isMasterStat) {
        // Master Stats should be filtered based on entity/department match
        return entitiesSelected.some(entity => entity.entity_id === candidateAccount.entityId) &&
               departmentsSelected.some(department => 
                 department.departmentId === candidateAccount.departmentId && 
                 department.entityId === candidateAccount.entityId
               );
      }
      
      // Regular account validation: check if at least ONE valid GL key can be formed 
      // with current entity/department selections using O(1) cache lookup
      for (const entity of entitiesSelected) {
        for (const department of departmentsSelected) {
          // Only check departments that belong to this entity
          if (department.entityId === entity.entity_id) {
            const glKey = `${entity.entity_id}-${department.departmentId}-${candidateAccount.id}`;
            if (glKeyCache.has(glKey)) {
              return true; // Early termination - found at least one valid combination
            }
          }
        }
      }
      
      return false;
    });
  }, [accountsOptions, entitiesSelected, departmentsSelected, glKeyCache]);

  /**
   * @param {Array} value Array of entities to populate options for the Entities Material UI Autocomplete component
   * @param {String} field Field value dictating which state variable to apply the change to
   * @implements Builds departmentOptions to populate the Departments Material UI Autocomplete component
   */
  const handleEntityChange = (value, field) => {
    if (!value || typeof value === "undefined" || value.length === 0) {
      if (field === "entitiesSelected"){
        setAccountsOptions([]);
        setAccountsSelected([]);
        setChangeDetailsState(prev => ({
          ...prev,
          selected_accounts: [],
          reference_accounts: [],
        }));
        setDepartmentsOptions([]);
        setDepartmentsSelected([]);
        setEntitiesSelected([]);
        setErrors(prev => ({
          ...prev,
          invalidGlKeys: [],
          glKeyValidationError: '',
        }));
      }
      if (field === "referenceEntitySelected"){
        setReference_accountsOptions([]);
        setReferenceAccountSelected(null);
        setReferenceEntitySelected(null);
      }
      return;
    }

    onEntitiesChange(value);

    switch(field){
      case "entitiesSelected":
        setAccountsOptions([]);
        // Re-resolve existing account selection against the new entity context.
        // Clears accounts that no longer apply and surfaces new invalid-key warnings.
        if (accountsSelected.length > 0 && departmentsSelected.length > 0) {
          const afd = context.activeProjectAttributes?.accountsMetadata || [];
          const { resolved, invalidGlKeys } = resolveGlKeys(
            accountsSelected, value, departmentsSelected, afd
          );
          setAccountsSelected(resolved);
          setErrors(prev => ({
            ...prev,
            invalidGlKeys,
            glKeyValidationError: invalidGlKeys.length > 0
              ? 'The following GL keys do not exist in the original dataset and have been removed:'
              : '',
          }));
        } else {
          setAccountsSelected([]);
          setErrors(prev => ({ ...prev, invalidGlKeys: [], glKeyValidationError: '' }));
        }
        setDepartmentsSelected([]);
        setEntitiesSelected(value || []);
        break;
      case "referenceEntitySelected":
        setReference_accountsOptions([]);
        setReferenceAccountSelected(null);
        setReferenceEntitySelected(value || []);
        break;
      default: break;
    }
  };

  /**
   * @param {Array} value Array of departments to populate options for the Departments Material UI Autocomplete component
   * @implements Builds accountOptions to populate an Accounts Material UI Autocomplete component
   */
  const handleDepartmentChange = (value, reference = false) => {
    if (!value || typeof value === "undefined" || value.length === 0) {
      setAccountsOptions([]);
      setAccountsSelected([]);
      setChangeDetailsState(prev => ({
        ...prev,
        selected_accounts: [],
        reference_accounts: [],
      }));
      setDepartmentsSelected([]);
      setErrors(prev => ({ ...prev, invalidGlKeys: [], glKeyValidationError: '' }));
      return;
    }

    const uniqueDepts = [...new Set(value)];
    onDepartmentSelected(uniqueDepts);
    setDepartmentsSelected(uniqueDepts);

    // Re-resolve existing account selection against the new department context.
    // This surfaces invalid-key warnings immediately when departments change.
    if (accountsSelected.length > 0 && entitiesSelected.length > 0) {
      const afd = context.activeProjectAttributes?.accountsMetadata || [];
      const { resolved, invalidGlKeys } = resolveGlKeys(
        accountsSelected, entitiesSelected, uniqueDepts, afd
      );
      setAccountsSelected(resolved);
      setErrors(prev => ({
        ...prev,
        invalidGlKeys,
        glKeyValidationError: invalidGlKeys.length > 0
          ? 'The following GL keys do not exist in the original dataset and have been removed:'
          : '',
      }));
    } else {
      setErrors(prev => ({ ...prev, invalidGlKeys: [], glKeyValidationError: '' }));
    }
  };

  /**
   * Handles a group select of Departments 
   * @param {Object} params Parameters of a Material UI Autocomplete component group
   * @param {String} field Field value dictating which state variable to apply the change to
   * @implements Builds an array of departments according to the Material UI Autocomplete group select. Sends the result to handleDepartmentChange.
   */
  const handleDepartmentGroupSelect = (params, field) => {
    const value = params.group;

    const departmentsOptionsSelected = departmentsOptions.filter((el) => el.entityName === value);
    const referenceDepartmentsOptionsSelected = referenceDepartmentsOptions.filter((el) => el.entityName === value);

    let groupSelection;

    switch(field){
      case "departmentsSelected":
        groupSelection = departmentsSelected;
        if(departmentsOptionsSelected.length > 0){
          groupSelection.push(...departmentsOptionsSelected);
        }
        break;
      case "referenceDepartmentSelected":
        groupSelection = referenceDepartmentSelected;
        if(referenceDepartmentsOptionsSelected.length > 0){
          groupSelection.push(...referenceDepartmentsOptionsSelected);
        }
        break;
      default:
        break;
    }
  
    handleDepartmentChange(groupSelection, field);
  };

  const handleReferenceDepartmentChange = (value) => {

    if (!value || typeof value === "undefined" || value.length === 0) {
      setReference_accountsOptions([]);
      setReferenceAccountSelected(null);
      setReferenceDepartmentSelected(null);
      // clearContextValues()
      return;
    }

    let referenceDepartmentsSelectedData = value;

    const uniquereferenceDepartmentsSelected = [referenceDepartmentsSelectedData];

    onDepartmentSelected(uniquereferenceDepartmentsSelected)

    setReferenceDepartmentSelected(uniquereferenceDepartmentsSelected[0]);
  };

  // ---------------------------------------------------------------------------
  // Account display helpers
  // Accounts in accountsSelected are accountsMetadata entries
  // ({ account_id, account_name, gl_key, ... }) while filteredAccountsOptions
  // are the deduplicated option objects ({ id, label, parentAccount, ... }).
  // These helpers normalise access so the Autocomplete and chip display work
  // with both shapes.
  // ---------------------------------------------------------------------------

  /** Returns the display label for any account object regardless of shape */
  const getAccountLabel = (account) => {
    if (!account) return '';
    // accountsMetadata shape
    if (account.account_name) return account.account_name;
    // option object shape
    if (account.label) return account.label;
    // Master Stat
    if (account.master_stat_name) return account.master_stat_name;
    return '';
  };

  /** Returns the account_id string for any account object regardless of shape */
  const getAccountId = (account) => {
    if (!account) return '';
    if (account.account_id != null) return String(account.account_id);
    if (account.id != null) return String(account.id);
    return '';
  };

  /**
   * Returns the parent account / group name for any account object.
   * Used by groupBy and handleAccountGroupSelect.
   */
  const getAccountParent = (account) => {
    if (!account) return '';
    return account.parent_account || account.parentAccount || '';
  };

  /**
   * Called whenever the accounts autocomplete value changes.
   * Resolves the selection to full accountsMetadata entries via resolveGlKeys
   * and stores them in accountsSelected. No Cartesian product, no stamping.
   */
  const handleAccountChange = (value) => {
    // Deduplicate by reference (MUI Autocomplete can fire with duplicates)
    const uniqueOptions = [...new Map(
      value.map(a => [a.isMasterStat ? a.id : `${a.id}`, a])
    ).values()];

    if (!uniqueOptions || uniqueOptions.length === 0) {
      setAccountsSelected([]);
      setSelectedMasterStats([]);
      setSelectedChildStatsWithParents([]);
      setErrors(prev => ({ ...prev, invalidGlKeys: [], glKeyValidationError: '' }));
      return;
    }

    // Reference account changes don't need GL key resolution
    if (function_operator === OPERATORS.REFERENCE) {
      setAccountsSelected(uniqueOptions);
      setErrors(prev => ({ ...prev, invalidGlKeys: [], glKeyValidationError: '' }));
      return;
    }

    const { activeProjectAttributes } = context;
    const accountsFullDetails = activeProjectAttributes?.accountsMetadata || [];

    // If no entities/departments selected yet, hold the raw options until they are
    if (!entitiesSelected?.length || !departmentsSelected?.length) {
      setAccountsSelected(uniqueOptions);
      setErrors(prev => ({ ...prev, invalidGlKeys: [], glKeyValidationError: '' }));
      return;
    }

    // Resolve to full accountsMetadata entries
    const { resolved, invalidGlKeys } = resolveGlKeys(
      uniqueOptions,
      entitiesSelected,
      departmentsSelected,
      accountsFullDetails,
    );

    // Track Master Stats and child stats for downstream use
    const masterStats = [];
    const childStatsWithParents = [];
    resolved.forEach(entry => {
      if (entry.isMasterStat) {
        const ms = activeProjectAttributes.masterStats?.find(m => m.gl_key === entry.id);
        if (ms) masterStats.push(ms);
      } else {
        const glKey = entry.gl_key;
        const parentMasterStats = (activeProjectAttributes.masterStats || []).filter(
          ms => ms.child_stats?.includes(glKey)
        );
        if (parentMasterStats.length > 0) {
          childStatsWithParents.push({
            childGlKey: glKey,
            childName: entry.account_name,
            parentMasterStats,
          });
        }
      }
    });

    setSelectedMasterStats(masterStats);
    setSelectedChildStatsWithParents(childStatsWithParents);
    setAccountsSelected(resolved);
    setErrors(prev => ({
      ...prev,
      invalidGlKeys,
      glKeyValidationError: invalidGlKeys.length > 0
        ? 'The following GL keys do not exist in the original dataset and have been removed:'
        : '',
    }));
  };

  /**
   * Handles a group header click in the accounts autocomplete.
   * Adds all accountsMetadata entries matching the parent account group
   * that are valid for the current entity/department selections.
   */
  const handleAccountGroupSelect = (params, _field) => {
    const parentAccount = params.group;
    const accountsFullDetails = context.activeProjectAttributes?.accountsMetadata || [];

    const entityIds  = new Set(entitiesSelected.map(e => String(e.entity_id)));
    const deptIds    = new Set(departmentsSelected.map(d => String(d.departmentId)));

    // Find all valid entries for this parent account group
    const groupEntries = accountsFullDetails.filter(entry =>
      entry.parent_account === parentAccount &&
      entityIds.has(String(entry.entity_id)) &&
      deptIds.has(String(entry.department_id))
    );

    // Merge with existing selection, deduplicating by gl_key
    const existingKeys = new Set(
      accountsSelected
        .filter(a => !a.isMasterStat)
        .map(a => a.gl_key)
    );
    const toAdd = groupEntries.filter(e => !existingKeys.has(e.gl_key));

    const merged = [...accountsSelected, ...toAdd];
    handleAccountChange(merged);
  };

  const handleReferenceAccountSelect = (account) => {
    setErrors(prev => ({
      ...prev,
      refAccountError: '',
    }));
    setReferenceAccountSelected(account);
  };

  const handleDominantChange = () => {
    setChangeDetailsState(prev => ({
      ...prev,
      is_dominant_change: !prev.is_dominant_change
    }));
  };

  const handleDateRangeChange = (value) => {
    setDateRangeValue(value);
    const startDate = format(new Date(value.from.year, value.from.month - 1, 1), 'yyyy-MM-dd');
    const endDate = format(new Date(value.to.year, value.to.month - 1, 1), 'yyyy-MM-dd');
    setChangeDetailsState(prev => ({
      ...prev,
      date_range: [startDate, endDate]
    }));
  };

  const openMonthPicker = () => {
    monthPickerRef.current.show();
  };

  const handleMonthPickerChange = (value, text, listIndex) => {
    // Implementation for month picker change if needed
  };

  const handleMonthPickerDissmis = (value) => {
    setMonthRangeValue(value);

    // Month picker uses 1-12 for months, JavaScript Date uses 0-11
    // Create dates for the FIRST day of each month
    const startDate = format(new Date(value.from.year, value.from.month - 1, 1), 'yyyy-MM-dd');
    const endDate = format(new Date(value.to.year, value.to.month - 1, 1), 'yyyy-MM-dd');

    const changeDetailsData = {
      ...changeDetailsState,
      date_range: [startDate, endDate]
    };

    if(changeDetailsData.function_operator === OPERATORS.REFERENCE && referenceGridData?.length > 0){
      buildReferenceAgGridTable(changeDetailsData);
    }

    setChangeDetailsState(changeDetailsData);
  };

  const handlePreviewChange = async () => {
    setIsSubmitting(true);
    // Clear any previous submit error
    setErrors(prev => ({ ...prev, submitError: '' }));
    try {
      await _handlePreviewChange();
    } catch (err) {
      // Unexpected throw — surface it as a submit error and clear loading state
      const msg = err?.message || 'An unexpected error occurred. Please try again.';
      setErrors(prev => ({ ...prev, submitError: msg }));
      onCalculatingFailed?.();
    } finally {
      setIsSubmitting(false);
    }
  };

  const _handlePreviewChange = async () => {

    const { activeProjectAttributes } = context;

    // accountsSelected now holds full accountsMetadata entries (or Master Stat
    // pass-throughs). GL keys are already attached — no construction needed.
    const selected_accounts_gl_keys = accountsSelected.map(entry =>
      entry.isMasterStat ? entry.id : entry.gl_key
    ).filter(Boolean);

    // Classify all selected GL keys into Master Stats, child-stat changes, and
    // regular stat drivers using the shared helper (same logic as the preview memo).
    // drivenAccounts in each entry are scoped to the driver's own dept — see
    // classifySelectedAccounts for details.
    const { affectedMasterStats, affectedByChildStatChanges, regularStatDrivers } =
      classifySelectedAccounts(
        selected_accounts_gl_keys,
        activeProjectAttributes.masterStats || [],
        activeProjectAttributes.accountsMetadata || [],
      );

    // Build driven_accounts for the change payload (GL keys of accounts driven by
    // any regular stat driver in the selection).
    let driven_accounts = [];
    regularStatDrivers.forEach(driver => {
      driver.drivenAccounts.forEach(acc => driven_accounts.push(acc.gl_key));
      // Mark the change as a stat driver if any regular driver is present
      setChangeDetailsState(prev => ({ ...prev, is_stat_driver: true }));
    });

    // Build the affectedStatDrivers array for the payload.
    const affectedStatDrivers = [];

    // Master Stats that are themselves drivers
    affectedMasterStats.forEach(masterStat => {
      if (isMasterStatDriver(masterStat, activeProjectAttributes.accountsMetadata)) {
        // Scope driven accounts to only the entity-departments the user selected
        // that contain this master stat driver, to avoid O(D²) bloat.
        const masterStatDept = masterStat.gl_key ? masterStat.gl_key.split('-').slice(0, 2).join('-') : null;
        const drivenAccounts = getDrivenAccountsByStatDriver(
          masterStat.gl_key,
          activeProjectAttributes.accountsMetadata
        ).filter(a =>
          !masterStatDept || `${a.entity_id}-${a.department_id}` === masterStatDept
        );
        affectedStatDrivers.push({
          type: 'master_stat',
          gl_key: masterStat.gl_key,
          name: masterStat.master_stat_name,
          drivenAccounts,
        });
      }

      // Child stats within the Master Stat that are also drivers
      getChildStatDrivers(masterStat, activeProjectAttributes.accountsMetadata)
        .forEach(childGlKey => {
          const childDept = childGlKey ? childGlKey.split('-').slice(0, 2).join('-') : null;
          const drivenAccounts = getDrivenAccountsByStatDriver(
            childGlKey,
            activeProjectAttributes.accountsMetadata
          ).filter(a =>
            !childDept || `${a.entity_id}-${a.department_id}` === childDept
          );
          affectedStatDrivers.push({
            type: 'child_stat',
            gl_key: childGlKey,
            parent_master_stat: masterStat.gl_key,
            drivenAccounts,
          });
        });
    });

    // Child stat changes that are themselves drivers
    const addedChildDriverKeys = new Set(affectedStatDrivers.map(d => d.gl_key));
    affectedByChildStatChanges.forEach(item => {
      const childDrivers = item.parentMasterStats.flatMap(ms =>
        getChildStatDrivers(ms, activeProjectAttributes.accountsMetadata)
      );
      if (childDrivers.includes(item.childGlKey) && !addedChildDriverKeys.has(item.childGlKey)) {
        const childDept = item.childGlKey ? item.childGlKey.split('-').slice(0, 2).join('-') : null;
        const drivenAccounts = getDrivenAccountsByStatDriver(
          item.childGlKey,
          activeProjectAttributes.accountsMetadata
        ).filter(a =>
          !childDept || `${a.entity_id}-${a.department_id}` === childDept
        );
        affectedStatDrivers.push({
          type: 'child_stat',
          gl_key: item.childGlKey,
          drivenAccounts,
        });
        addedChildDriverKeys.add(item.childGlKey);
      }
    });

    // Regular stat drivers (already fully built by classifySelectedAccounts)
    affectedStatDrivers.push(...regularStatDrivers);

    // If this change involves stat drivers OR is a master stat change, signal the
    // grid to start showing the loading state NOW — before the API call — so the
    // full API + calculation window is covered. We know this synchronously before
    // any network activity.
    //
    // Master stat changes are particularly important here: the backend does async
    // distribution calculation that addChange polls for up to ~37s. Without an
    // early signal the user would see no column indicator for that entire window.
    const isStatDriverChange = affectedStatDrivers.length > 0;
    const isMasterStatChange = affectedMasterStats.length > 0;
    if (isStatDriverChange || isMasterStatChange) {
      onCalculatingStart?.(changeDetailsState.change_label);
    }

    // Calculate Master Stat distribution if this is a Master Stat change
    let datesObject = {};
    
    if (affectedMasterStats.length > 0) {
      // Get grid rows for calculation
      const gridRows = context.buildAgGridTable();
      
      // For each affected Master Stat, calculate the distribution
      affectedMasterStats.forEach(masterStat => {
        // Create a temporary change object for calculation
        const tempChange = {
          function_operator: changeDetailsState.function_operator,
          operator_value: parseFloat(changeDetailsState.operator_value) || 0,
          date_range: changeDetailsState.date_range,
        };
        
        // Calculate distribution for all months
        const monthlyAdjustments = calculateMasterStatChangeAllMonths(
          tempChange,
          masterStat,
          gridRows
        );
        
        // Convert monthly adjustments to dates object format
        // The backend expects: { "child_gl_key": { "2023-01-01": value, ... } }
        Object.entries(monthlyAdjustments).forEach(([month, adjustments]) => {
          adjustments.forEach(adjustment => {
            if (!datesObject[adjustment.gl_key]) {
              datesObject[adjustment.gl_key] = {};
            }
            datesObject[adjustment.gl_key][month] = adjustment.change_amount;
          });
        });
      });
    }
    
    const changeDetailsData = {
      ...changeDetailsState,
      driven_accounts: driven_accounts,
      selected_accounts: selected_accounts_gl_keys,
      dates: Object.keys(datesObject).length > 0 ? datesObject : undefined,
      
      // Master Stat specific fields
      is_master_stat_change: affectedMasterStats.length > 0,
      master_stat_gl_keys: affectedMasterStats.map(ms => ms.gl_key),
      affected_master_stats: affectedMasterStats,
      child_stat_adjustments: affectedMasterStats.map(ms => ({
        master_stat_gl_key: ms.gl_key,
        child_stats: ms.child_stats,
      })),
      
      // Child stat changes that affect Master Stats
      affects_master_stats_via_children: affectedByChildStatChanges.length > 0,
      affected_master_stats_by_children: affectedByChildStatChanges,
      
      // Stat driver integration
      affects_stat_drivers: affectedStatDrivers.length > 0,
      affected_stat_drivers: affectedStatDrivers,
    }

    // Check if we're editing an existing change
    const isEditing = changeToEdit && changeToEdit.id;
    
    if (isEditing) {
      // For edits, call previewChange and wait for it to complete
      const success = await previewChange(changeDetailsData);
      
      // Only proceed if edit was successful
      if (success) {
        // Navigate back to project home - success banner is shown there
        cancelChange();
      } else {
        onCalculatingFailed?.();
        setErrors(prev => ({
          ...prev,
          submitError: 'Unable to save changes. Please try again, or refresh the page if the problem persists.',
        }));
      }
    } else {
      // For new changes, wait for the response before clearing the form
      const success = await previewChange(changeDetailsData);
      
      // Only clear the form if the change was successfully created
      if (success) {
        clearChangeForm();
      } else {
        // Failed — clear loading state and surface the error inline
        onCalculatingFailed?.();
        setErrors(prev => ({
          ...prev,
          submitError: 'Failed to create bridge column. Please try again, or refresh the page if the problem persists.',
        }));
      }
      // If failed, form data is preserved so the user can retry
    }
  };

  const handleCancelChange = () => {
    clearChangeForm();
    cancelChange();
  };

  const prefillData = (type) => {
    let data;
    switch(type){
      case 'basicChange':
        data = basicChange;
        break;
      case 'statDriver':
        data = statDriver;
        break;
      case 'referenceAccount':
        data = referenceAccountDominant;
        break; 
      default:
        data = defaultState;
        break; 
    }
    // Note: Need to set all individual state pieces instead of spreading
    setAccountsOptions(data.accountsOptions || []);
    setAccountsSelected(data.accountsSelected || []);
    setChangeDetailsState(data.changeDetails || defaultState.changeDetails);
    setDateRangeValue(data.dateRangeValue || defaultState.dateRangeValue);
    setDepartmentsOptions(data.departmentsOptions || []);
    setDepartmentsSelected(data.departmentsSelected || []);
    setEntitiesOptions(data.entitiesOptions || []);
    setEntitiesSelected(data.entitiesSelected || []);
    setErrors(data.errors || {});
    setMaxDate(data.maxDate || defaultState.maxDate);
    setMinDate(data.minDate || defaultState.minDate);
    setMonthRangeValue(data.monthRangeValue || defaultState.monthRangeValue);
    setMonthPickerRange(data.monthPickerRange || defaultState.monthPickerRange);
    setParentChangeOptions(data.parentChangeOptions || []);
    setReference_accountsOptions(data.reference_accountsOptions || []);
    setReferenceAccountSelected(data.referenceAccountSelected || null);
    setReferenceGridData(data.referenceGridData || false);
    setReferenceDepartmentsOptions(data.referenceDepartmentsOptions || []);
    setReferenceDepartmentSelected(data.referenceDepartmentSelected || null);
    setReferenceEntitiesOptions(data.referenceEntitiesOptions || []);
    setReferenceEntitySelected(data.referenceEntitySelected || null);
  };

  /** ----------------
   * 
   * AG Grid Functions
   * 
   * ----------------- */

  /**
   * Adds a reference account to the change form.
   *
   * Fetches full account data (including dates) via `getAccountsByGlKeys` instead
   * of using `buildAgGridTable()` so that `basePeriodDates` is always populated.
   */
  const addReferenceAccount = async () => {
    const accessToken = activeUser?.accessToken;
    const projectId = context.activeProjectInfo?.id;
    const refAccountGlKey = `${referenceEntitySelected.entity_id}-${referenceDepartmentSelected.departmentId}-${referenceAccountSelected.id}`;

    const refAccountIndex = (changeDetailsState.reference_accounts || []).findIndex(acc => acc.glKey === refAccountGlKey);

    if (refAccountIndex !== -1) {
      setErrors(prev => ({
        ...prev,
        refAccountError: `Account ${referenceAccountSelected.id} - ${referenceAccountSelected.label} is already being used as a reference account. Please select a different account.`,
      }));
      return;
    }

    // Fetch the real account data (with dates) for this GL key.
    let accountDates = {};
    if (accessToken && projectId) {
      try {
        const response = await getAccountsByGlKeys(projectId, [refAccountGlKey], accessToken);
        if (response?.status === 200 && response?.data?.accounts?.length > 0) {
          accountDates = response.data.accounts[0].dates || {};
        } else {
          setErrors(prev => ({
            ...prev,
            refAccountError: `Reference account "${referenceAccountSelected.label}" (${refAccountGlKey}) not found in project data. Please ensure the account exists in the base period data.`,
          }));
          return;
        }
      } catch (err) {
        console.error('[addReferenceAccount] Failed to fetch account dates:', err);
        setErrors(prev => ({
          ...prev,
          refAccountError: `Failed to load data for reference account "${referenceAccountSelected.label}". Please try again.`,
        }));
        return;
      }
    }

    const updatedChangeDetails = cloneDeep(changeDetailsState);
    let rowLetter;

    if ((changeDetailsState.reference_accounts || []).length > 0) {
      const lastRowLetter = changeDetailsState.reference_accounts[changeDetailsState.reference_accounts.length - 1].rowLetter;
      rowLetter = getNextExcelColumn(lastRowLetter);
    } else {
      rowLetter = 'B';
    }

    updatedChangeDetails.reference_accounts.push({
      rowLetter,
      glKey: refAccountGlKey,
      dates: accountDates,
    });

    // Look up display name from metadata (no dates needed here)
    const metaAccount = (context.activeProjectAttributes?.accountsMetadata || [])
      .find(a => a.gl_key === refAccountGlKey);
    const accountName = metaAccount
      ? `${metaAccount.account_id} - ${metaAccount.account_name}`
      : refAccountGlKey;

    const newRow = buildNormalisedRefRow(refAccountGlKey, rowLetter, accountDates, { accountName });

    setChangeDetailsState(updatedChangeDetails);
    setErrors(prev => ({ ...prev, refAccountError: '' }));
    setReferenceGridData(prev => Array.isArray(prev) ? [...prev, newRow] : [newRow]);
  };



  /**
   * AG Grid event. Resize all columns when data is rendered
   */
  const onFirstDataRendered = () => {
    const allColumnIds = [];
    gridRef.current.columnApi.getColumns().forEach((column) => {
      allColumnIds.push(column.getId());
    });
    gridRef.current.columnApi.autoSizeColumns(allColumnIds, false);
  };


  const { activeProjectInfo } = context;

  const {
    change_label,
    date_range,
    function_operator,
    is_dominant_change,
    operator_value,
    parent_change,
    ref_account_formula,
  } = changeDetailsState;

  const accountsSelectedList = accountsSelected.map((account, i) => {
    return <ListItem
              key={i}
              sx={{
                padding: '0',
              }}
            >
              <ListItemText
                primary={getAccountLabel(account)}
                secondary={account.gl_key || getAccountId(account)}
                sx={{
                  mt: '0',
                }}
              />
            </ListItem>
  });

  const showRefAccountError = typeof errors.refAccountError !== 'undefined' && errors.refAccountError.length > 0;

  const previewButtonEnabled =
    change_label.length > 0 &&
    function_operator.length > 0 &&
    date_range &&
    (operator_value || (referenceAccountSelected && ref_account_formula)) &&
    accountsSelected.length > 0; // Ensure at least one valid account is selected

  const makeText = m => {
    if (m && m.year && m.month) return (pickerLang.months[m.month - 1] + '. ' + m.year)
    return '?'
  }

  const accountFilter = createFilterOptions({
    matchFrom: 'any',
    stringify: (option) => `${option.label} ${option.parentAccount}`,
  });

  const tagLimit = 3;

  if(activeProjectInfo){
    return (
        <Paper
          elevation={3}
          sx={{
            mb: '2rem',
            padding: '20px',
          }}
        >
          <Grid
            item
            container
            spacing={4}
            xs={12}
          >
            <Grid 
              item 
              xs={12}
            >
              <Typography
                align="left"
                component="h2"
                color="inherit"
                sx={{
                  fontSize: '1.2rem',
                  mb: '1rem'
                }}
              >
                Change Details
              </Typography>
            </Grid>
            
            {/* Master Stat Change Alert */}
            {selectedMasterStats.length > 0 && (
              <Grid item xs={12}>
                <Alert severity="info" sx={{ mb: 2 }}>
                  <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
                    Master Stat Change Detected
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    Changes will be applied to the following Master Stats and distributed proportionally to their child stats:
                  </Typography>
                  {selectedMasterStats.map((ms, idx) => {
                    const { activeProjectAttributes } = context;
                    const childAccounts = ms.child_stats?.map(glKey => {
                      const account = activeProjectAttributes.accountsMetadata?.find(
                        acc => `${acc.entity_id}-${acc.department_id}-${acc.account_id}` === glKey
                      );
                      return account ? {
                        glKey,
                        name: account.account_name,
                        entity: account.entity_name,
                        department: account.department_name
                      } : null;
                    }).filter(Boolean) || [];
                    
                    const hasMany = childAccounts.length > 6;
                    
                    return (
                      <Box key={idx} sx={{ mb: idx < selectedMasterStats.length - 1 ? 2 : 0 }}>
                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                          {ms.master_stat_name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                          {childAccounts.length} child stat{childAccounts.length !== 1 ? 's' : ''} will be updated proportionally:
                        </Typography>
                        <Box 
                          sx={{ 
                            maxHeight: hasMany ? '200px' : 'none',
                            overflowY: hasMany ? 'auto' : 'visible',
                            border: hasMany ? '1px solid #e0e0e0' : 'none',
                            borderRadius: hasMany ? '4px' : '0',
                            padding: hasMany ? '8px' : '0',
                            backgroundColor: hasMany ? '#fafafa' : 'transparent'
                          }}
                        >
                          <ul style={{ marginTop: 0, marginBottom: 0, paddingLeft: '20px' }}>
                            {childAccounts.map((child, childIdx) => (
                              <li key={childIdx}>
                                <Typography variant="caption">
                                  <strong>{child.name}</strong>
                                  <br />
                                  <span style={{ color: '#666' }}>
                                    {child.entity} / {child.department} ({child.glKey})
                                  </span>
                                </Typography>
                              </li>
                            ))}
                          </ul>
                        </Box>
                      </Box>
                    );
                  })}
                </Alert>
              </Grid>
            )}
            
            {/* Child Stat Changes Alert */}
            {selectedChildStatsWithParents.length > 0 && (
              <Grid item xs={12}>
                <Alert severity="info" sx={{ mb: 2 }}>
                  <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
                    Child Stat Changes Detected
                  </Typography>
                  <Typography variant="body2">
                    The following Master Stats will be recalculated to reflect the sum of their child stats:
                  </Typography>
                  <ul style={{ marginTop: '8px', marginBottom: 0 }}>
                    {(childStatAlertExpanded
                      ? getUniqueMasterStatsFromChildren
                      : getUniqueMasterStatsFromChildren.slice(0, ALERT_LIST_PREVIEW)
                    ).map((ms) => (
                      <li key={ms.gl_key}>
                        <strong>{ms.master_stat_name}</strong>
                      </li>
                    ))}
                  </ul>
                  {getUniqueMasterStatsFromChildren.length > ALERT_LIST_PREVIEW && (
                    <Button
                      size="small"
                      onClick={() => setChildStatAlertExpanded(prev => !prev)}
                      startIcon={childStatAlertExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                      sx={{ mt: 1, p: 0, textTransform: 'none', color: 'inherit' }}
                    >
                      {childStatAlertExpanded
                        ? 'Show less'
                        : `Show ${getUniqueMasterStatsFromChildren.length - ALERT_LIST_PREVIEW} more`}
                    </Button>
                  )}
                </Alert>
              </Grid>
            )}
            
            {/* Stat Driver Changes Alert (Phase 5) */}
            {affectedStatDriversPreview.length > 0 && (
              <Grid item xs={12}>
                <Alert severity="warning" sx={{ mb: 2 }}>
                  <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
                    Stat Driver Changes Detected
                  </Typography>
                  <Typography variant="body2">
                    This change will trigger recalculation of driven variable accounts:
                  </Typography>
                  <ul style={{ marginTop: '8px', marginBottom: 0 }}>
                    {(statDriverAlertExpanded
                      ? affectedStatDriversPreview
                      : affectedStatDriversPreview.slice(0, ALERT_LIST_PREVIEW)
                    ).map((driver, idx) => (
                      <li key={idx}>
                        <strong>{driver.name}</strong> drives {driver.drivenAccounts.length} account(s)
                      </li>
                    ))}
                  </ul>
                  {affectedStatDriversPreview.length > ALERT_LIST_PREVIEW && (
                    <Button
                      size="small"
                      onClick={() => setStatDriverAlertExpanded(prev => !prev)}
                      startIcon={statDriverAlertExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                      sx={{ mt: 1, p: 0, textTransform: 'none', color: 'inherit' }}
                    >
                      {statDriverAlertExpanded
                        ? 'Show less'
                        : `Show ${affectedStatDriversPreview.length - ALERT_LIST_PREVIEW} more`}
                    </Button>
                  )}
                </Alert>
              </Grid>
            )}
            
            
            <Grid
              item
              sx={{
                display: 'flex',
                justifyContent: 'flex-start',
              }}
              xs={4}
            >
              <TextField
                id="change_label"
                label="Change Label"
                onChange={handleChange}
                value={change_label}
                variant="outlined"
                sx={{
                  mb: '20px',
                  width: '435px'
                }}
              />
            </Grid>
            <Grid 
              item 
              xs={12}
              md={4}
            >
              <Autocomplete
                freeSolo={true}
                id="parent_changes"
                key="parent_changes"
                options={parentChangeOptions}
                onChange={(event, newValue) => { handleparent_change(newValue)}}
                onInputChange={(event, newValue) => { handleparent_change(newValue)}}
                renderInput={(params) => <TextField {...params} label="Parent Change" name={'parent_change'} />}
                sx={{
                  mb: '20px',
                  width: '100%'
                }}
                value={parent_change}
              />
            </Grid>
            <Grid item xs={12} md={4} /> {/* Spacer to push Entity/Department/Account to next row */}
            <Grid item xs={12} md={4}>
              <Autocomplete
                disableCloseOnSelect
                filterSelectedOptions
                getOptionLabel={(entity) => `${entity.entity_id} - ${entity.entity_name}`}
                id="entity"
                key="entity"
                isOptionEqualToValue={(option, value) => option.entity_id === value.entity_id}
                limitTags={tagLimit}
                multiple
                onChange={(event, newValue) => { handleEntityChange(newValue, "entitiesSelected") }}
                options={entitiesOptions}
                renderInput={(params) => <TextField {...params} label="Entity(s) to change" name={'entity'} />}
                sx={{ minWidth: 250, width: '100%' }}
                value={entitiesSelected}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <Autocomplete
                disableCloseOnSelect
                filterSelectedOptions
                options={departmentsOptions}
                getOptionLabel={(department) => `${department.departmentId} - ${department.departmentName}`}
                groupBy={(department) => department.entityName}
                id="department"
                key="department"
                isOptionEqualToValue={(option, value) =>
                  option.departmentId === value.departmentId && option.entityId === value.entityId
                }
                limitTags={tagLimit}
                multiple
                noOptionsText="Select an Entity to populate this list"
                onChange={(event, newValue, reason) => { handleDepartmentChange(newValue, "departmentsSelected") }}
                renderGroup={(params) => (
                  <li key={params.key}>
                    <GroupHeader
                      onClick={() => handleDepartmentGroupSelect(params, "departmentsSelected")}
                    >
                      {params.group}
                    </GroupHeader>
                    <GroupItems>{params.children}</GroupItems>
                  </li>
                )}
                renderInput={(params) => <TextField {...params} label="Department(s) to change" name={'department'} />}
                renderOption={(props, department, { selected }) => {
                  const { key, ...restProps } = props;
                  if (department.departmentName === 'All') {
                    restProps['aria-selected'] = false;
                    return (
                      <SelectAll key={key} {...restProps}>
                        {department.departmentName}
                      </SelectAll>
                    );
                  }
                  else {
                    return (
                      <li key={key} {...restProps}>
                        {`${department.departmentId} - ${department.departmentName}`}
                      </li>
                    );
                  }
                }}
                sx={{ minWidth: 250, width: '100%' }}
                value={departmentsSelected}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <Autocomplete
                disableCloseOnSelect
                filterOptions={accountFilter}
                filterSelectedOptions
                getOptionLabel={(account) => {
                  if (account) {
                    return `${getAccountId(account)} - ${getAccountLabel(account)}`;
                  }
                  return '';
                }}
                groupBy={(account) => getAccountParent(account)}
                id="account"
                key="account"
                isOptionEqualToValue={(option, value) => {
                  // option comes from filteredAccountsOptions (id/label shape)
                  // value comes from accountsSelected (accountsMetadata shape)
                  // Match on account_id since that's the common key
                  return String(getAccountId(option)) === String(getAccountId(value));
                }}
                limitTags={tagLimit}
                multiple
                noOptionsText="Select a Department to populate this list"
                onChange={(event, newValue) => { handleAccountChange(newValue, "accountsSelected") }}
                options={filteredAccountsOptions}
                renderGroup={(params) => (
                  <li key={params.key}>
                    <GroupHeader
                      onClick={() => handleAccountGroupSelect(params, "accountsSelected")}
                    >
                      {params.group}
                    </GroupHeader>
                    <GroupItems>{params.children}</GroupItems>
                  </li>
                )}
                renderInput={(params) => <TextField {...params} label="Account(s) to change" name={'account'} />}
                renderOption={(props, account, { selected }) => {
                  const { key, ...restProps } = props;
                  return (
                    <li key={key} {...restProps}>
                      {`${getAccountId(account)} - ${getAccountLabel(account)}`}
                    </li>
                  );
                }}
                renderTags={(tagValue, getTagProps) => {
                  // Deduplicate chips by account_id — accountsSelected holds one entry per
                  // GL key so the same account appears N times when N departments are selected.
                  // We still keep all GL key entries in accountsSelected so GL key resolution
                  // and submission work correctly; we only collapse the visual chips here.
                  const seen = new Set();
                  const dedupedTags = tagValue.filter((account) => {
                    const id = getAccountId(account);
                    if (seen.has(id)) return false;
                    seen.add(id);
                    return true;
                  });
                  // Respect limitTags: show up to tagLimit chips, then a "+N" chip
                  const visible = dedupedTags.slice(0, tagLimit);
                  const overflow = dedupedTags.length - visible.length;
                  return (
                    <>
                      {visible.map((account, index) => {
                        const { key, ...tagProps } = getTagProps({ index });
                        return (
                          <Chip
                            key={getAccountId(account)}
                            label={`${getAccountId(account)} - ${getAccountLabel(account)}`}
                            size="small"
                            {...tagProps}
                          />
                        );
                      })}
                      {overflow > 0 && (
                        <Chip label={`+${overflow}`} size="small" />
                      )}
                    </>
                  );
                }}
                sx={{ minWidth: 250, width: '100%' }}
                value={accountsSelected}
              />
              {/* Summary counter — always shown when at least one account is selected */}
              {(() => {
                const uniqueAccountIds = new Set(accountsSelected.filter(a => !a.isMasterStat).map(a => getAccountId(a)));
                const uniqueDeptCount = departmentsSelected.length;
                const uniqueAccountCount = uniqueAccountIds.size;
                const totalGlKeys = accountsSelected.filter(a => !a.isMasterStat).length;
                if (uniqueAccountCount === 0) return null;
                return (
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                    {uniqueAccountCount} account{uniqueAccountCount !== 1 ? 's' : ''} selected across {uniqueDeptCount} department{uniqueDeptCount !== 1 ? 's' : ''}: <strong>{totalGlKeys} GL Key{totalGlKeys !== 1 ? 's' : ''}</strong>
                  </Typography>
                );
              })()}
            </Grid>
            {/* GL Key Validation Error Alert */}
            {errors.glKeyValidationError && (
              <Grid item xs={12}>
                <Collapse in={Boolean(errors.glKeyValidationError)}>
                  <Alert 
                    severity="warning"
                    sx={{
                      mb: '1rem',
                    }}
                  >
                    <strong>Some Account + Department Combinations Were Removed</strong>
                    <br />
                    When selecting multiple departments and accounts, not every account exists in
                    every department. The following combinations were not found in the original
                    dataset and have been removed from your selection. Only valid combinations
                    will be applied.
                    {errors.invalidGlKeys && errors.invalidGlKeys.length > 0 && (
                      <Box
                        sx={{
                          mt: 1,
                          maxHeight: '160px',
                          overflowY: 'auto',
                          border: '1px solid rgba(0,0,0,0.12)',
                          borderRadius: 1,
                          bgcolor: 'rgba(0,0,0,0.03)',
                          px: 1,
                          py: 0.5,
                        }}
                      >
                        <ul style={{ margin: 0, paddingLeft: '1.25rem' }}>
                          {errors.invalidGlKeys.map((item, index) => (
                            <li key={index} style={{ fontSize: '0.85rem' }}>
                              <strong>{item.entityName}/{item.departmentName}/{item.accountName}</strong>
                              {' '}
                              <span style={{ opacity: 0.7 }}>({item.glKey})</span>
                            </li>
                          ))}
                        </ul>
                      </Box>
                    )}
                  </Alert>
                </Collapse>
              </Grid>
            )}
            <Grid
              item
              xs={6}
              sx={{
                flexBasis: 'calc(50% - 40px)',
                marginBottom: '20px',
                textAlign: 'left',
              }}
            >
              <FormControl>
                <FormLabel id="function-operator-button-group">
                  Adjust by absolute value, percentage, or reference account?
                  <ContextualTooltip
                    content="Choose how this change is applied: Absolute Value adds/subtracts a fixed amount, Percentage multiplies by a factor, Reference Account links to another account's values."
                    articleId="bridge-columns"
                    placement="right"
                  />
                </FormLabel>
                <RadioGroup
                  aria-labelledby="function-operator-button-group"
                  name="function_operator"
                  id="function_operator"
                  value={function_operator}
                  onChange={handleChange}
                >
                  <FormControlLabel value="absoluteValue" control={<Radio />} label="Absolute Value" />
                  <FormControlLabel value="percentage" control={<Radio />} label="Percentage" />
                  <FormControlLabel value="referenceAccount" control={<Radio />} label="Reference Account" />
                </RadioGroup>
              </FormControl>

              <Stack direction="row" spacing={1} alignItems="center">
                <Typography>Standard Change</Typography>
                <Switch
                  checked={is_dominant_change}
                  onChange={handleDominantChange}
                  inputProps={{ 'arial-label': 'controlled' }}
                />
                <Typography>Dominant Change</Typography>
                <Tooltip
                  placement="top"
                  title={
                    <React.Fragment>
                      <b>Standard Changes</b> use the uploaded Base Period value as the point of reference.
                      <br /><br />
                      <b>Dominant Changes</b> reference the Forecast calculation as the point of reference. 
                      Dominant Changes will be recalculated as new changes are added to the same GL key.
                    </React.Fragment>
                  }
                >
                  <HelpOutlineIcon
                    fontSize="small"
                    sx={{
                      color: "#c2c2c2",
                      cursor: "pointer"
                    }}
                  />
                </Tooltip>
              </Stack>
            </Grid>

            {function_operator && (
              <Grid
                item
                xs={6}
                sx={{
                  marginBottom: '20px',
                  textAlign: 'left'
                }}
              >
                <FormLabel id="date-range-button-group">
                  Select the date range for this change
                  <ContextualTooltip
                    content="Set the start and end months for this change. The change will be applied to all months within this range."
                    articleId="bridge-columns"
                    placement="right"
                  />
                </FormLabel>
                <Picker
                  key={`${monthRangeValue.from.year}-${monthRangeValue.from.month}-${monthRangeValue.to.year}-${monthRangeValue.to.month}`}
                  ref={monthPickerRef}
                  years={monthPickerRange}
                  value={monthRangeValue}
                  lang={pickerLang}
                  onChange={handleMonthPickerChange}
                  onDismiss={handleMonthPickerDissmis}
                >
                  <div className='month-range-input' onClick={openMonthPicker}>{makeText(monthRangeValue.from) + ' - ' + makeText(monthRangeValue.to)}</div>
                  {/* <MonthBox value={makeText(monthRangeValue.from) + ' ~ ' + makeText(monthRangeValue.to)} onClick={_handleClickRangeBox} /> */}

                </Picker>
              </Grid>
            )}
            {function_operator === OPERATORS.REFERENCE && (
              <>
                <Grid
                  item
                  sx={{
                    display: 'flex',
                    alignItems: 'flex-end',
                  }}
                  xs={12}
                >
                  <Typography 
                    id="reference-account-label"
                    align="left"
                    component="h3"
                    color="inherit"
                    sx={{
                      fontSize: '1.1rem',
                      mb: '1rem',
                    }}
                  >
                    Select an account to reference
                  </Typography>
                </Grid>
                <Grid
                  item
                  lg={4}
                  md={12}
                >
                  <Autocomplete
                    disableCloseOnSelect
                    filterSelectedOptions
                    getOptionLabel={(entity) => `${entity.entity_id} - ${entity.entity_name}`}
                    id="referenceEntity"
                    key="referenceEntity"
                    limitTags={tagLimit}
                    onChange={(event, newValue) => { handleEntityChange(newValue, "referenceEntitySelected") }}
                    options={referenceEntitiesOptions}
                    renderInput={(params) => <TextField {...params} label="Entity(s) to reference" name={'referenceEntity'} />}
                    sx={{ minWidth: 250 }}
                    value={referenceEntitySelected}
                  />
                </Grid>
                <Grid
                  item
                  lg={4}
                  md={12}
                >
                  <Autocomplete
                    filterSelectedOptions
                    options={referenceDepartmentsOptions}
                    getOptionLabel={(department) => `${department.departmentId} - ${department.departmentName}`}
                    groupBy={(department) => department.entityName}
                    id="referenceDepartment"
                    key="referenceDepartment"
                    limitTags={tagLimit}
                    noOptionsText="Select a Reference Entity to populate this list"
                    onChange={(event, newValue, reason) => { handleReferenceDepartmentChange(newValue) }}
                    renderGroup={(params) => (
                      <li key={params.key}>
                        <GroupHeader
                          onClick={() => handleDepartmentGroupSelect(params, "referenceDepartmentSelected")}
                        >
                          {params.group}
                        </GroupHeader>
                        <GroupItems>{params.children}</GroupItems>
                      </li>
                    )}
                    renderInput={(params) => <TextField {...params} label="Department(s) to reference" name={'referenceDepartment'} />}
                    renderOption={(props, department, { selected }) => {
                      const { key, ...restProps } = props;
                      if (department.departmentName === 'All') {
                        restProps['aria-selected'] = false;
                        return (
                          <SelectAll key={key} {...restProps}>
                            {department.departmentName}
                          </SelectAll>
                        );
                      }
                      else {
                        return (
                          <li key={key} {...restProps}>
                            {`${department.departmentId} - ${department.departmentName}`}
                          </li>
                        );
                      }
                    }}
                    sx={{ minWidth: 250 }}
                    value={referenceDepartmentSelected}
                  />
                </Grid>
                <Grid
                  item
                  lg={4}
                  md={12}
                >
                  <Autocomplete
                    defaultValue={""}
                    filterOptions={accountFilter}
                    filterSelectedOptions
                    groupBy={(account) => account.parentAccount}
                    getOptionLabel={(account) => {
                      if (account) {
                        return `${account.id} - ${account.label}`;
                      }
                    }}
                    id="reference_accounts"
                    key="reference_accounts"
                    limitTags={tagLimit}
                    noOptionsText={`Select a Reference Department to populate this list`}
                    onChange={(event, newValue) => { handleReferenceAccountSelect(newValue) }}
                    options={reference_accountsOptions}
                    renderGroup={(params) => (
                      <li key={params.key}>
                        <GroupHeader
                          onClick={() => handleAccountGroupSelect(params, "accountsSelected")}
                        >
                          {params.group}
                        </GroupHeader>
                        <GroupItems>{params.children}</GroupItems>
                      </li>
                    )}
                    renderInput={(params) => <TextField {...params} label="Account(s) to reference" name={'referenceAccount'} />}
                    renderOption={(props, account, { selected }) => {
                      const { key, ...restProps } = props;
                      return (
                        <li key={key} {...restProps}>
                          {`${account.id} - ${account.label}`}
                        </li>
                      );
                    }}
                    value={referenceAccountSelected}
                  />
                </Grid>
                <Collapse 
                  in={showRefAccountError}
                >
                  <Alert 
                    severity="error"
                    sx={{
                      ml: '2rem',
                      mt: '1rem',
                    }}
                  >
                    {errors.refAccountError}
                  </Alert>
                </Collapse>
                <Grid
                  item
                  sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                  }}
                  xs={12}
                >
                  <Button
                    color={"success"}
                    disabled={!referenceAccountSelected}
                    onClick={() => {addReferenceAccount()}}
                    startIcon={<AddCircleIcon />}
                    sx={{
                      mr: '1rem'
                    }}
                    variant="contained"
                  >
                    Add Ref. Account
                  </Button>
                </Grid>
                {referenceGridData &&
                  <Grid item xs={12}>
                    <>
                      <div className="ag-theme-alpine change-details-ag-grid" style={{ width: '100%', textAlign: 'left' }}>
                        <AgGridReact
                          animateRows={true} // Optional - set to 'true' to have rows animate when sorted
                          columnDefs={referenceColumns} // Column Defs for Columns
                          getRowId={getRowId} // Manually assign Row IDs
                          onFirstDataRendered={onFirstDataRendered}
                          ref={gridRef} // Ref for accessing Grid's API
                          rowData={referenceGridData} // Row Data for Rows
                          domLayout='autoHeight'
                          suppressSizeToFit={true}
                        />
                      </div>
                    </>
                </Grid>
                }
                {accountsSelectedList.length > 0 &&
                  <Grid
                    item
                    xs={2}
                  >
                    <List
                      disablePadding
                      sx={{
                        height: '170px',
                        overflow: 'scroll',
                      }}
                    >
                      {accountsSelectedList}
                    </List>
                  </Grid>
                }
                {(changeDetailsState.reference_accounts || []).length > 0 &&
                  <Grid
                    item
                    sx={{
                      justifyContent: 'left',
                      textAlign: 'left',
                    }}
                    xs={6}
                  >
                    <TextField
                      id="ref_account_formula"
                      label="Reference account formula"
                      onChange={handleChange}
                      variant="outlined"
                      InputProps={{
                        startAdornment: <InputAdornment position="start">=</InputAdornment>,
                      }}
                      sx={{
                        // mb: '20px',
                        width: '50%'
                      }}
                      value={ref_account_formula}
                    />
                    <p>
                      Use 'A' to represent the account to be changed in the formula. Use the corresponding row letter variable to represent the reference account value. e.g. A+(B*0.2) 
                      <br/>
                      <br/>
                      The calculation will be made for each month in the selected date range.
                    </p>
                  </Grid>
                }
              </>
            )}
            {function_operator.length > 0 && function_operator !== OPERATORS.REFERENCE && date_range && (
              <Grid
                item
                sx={{
                  display: 'flex',
                  justifyContent: 'flex-start',
                }}
                xs={12}
              >
                <TextField
                  id="operator_value"
                  label="Operator Value"
                  onChange={handleChange}
                  value={operator_value}
                  variant="outlined"
                  sx={{
                    mb: '20px',
                    width: '435px'
                  }}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">{function_operator === OPERATORS.PERCENTAGE ? '%' : ''}</InputAdornment>,
                  }}
                />
              </Grid>
            )}
            {/* Initial Comment Field - for new changes */}
            {!changeDetailsState.id && (
              <Grid item xs={12}>
                <Box sx={{ mt: 3, mb: 2 }}>
                  <Typography 
                    variant="h6" 
                    sx={{ mb: 2, borderBottom: '1px solid #ddd', pb: 1 }}
                  >
                    Add Initial Comment (Optional)
                  </Typography>
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    placeholder="Add a comment to explain this change..."
                    value={changeDetailsState.user_note || ''}
                    onChange={(e) => setChangeDetailsState(prev => ({
                      ...prev,
                      user_note: e.target.value
                    }))}
                    inputProps={{ maxLength: 1000 }}
                    helperText={`${(changeDetailsState.user_note || '').length}/1000 characters`}
                  />
                </Box>
              </Grid>
            )}
            
            {/* Comments & History Section - only show for existing changes */}
            {changeDetailsState.id && (
              <Grid item xs={12}>
                <Box sx={{ mt: 3, mb: 2 }}>
                  <Typography 
                    variant="h6" 
                    sx={{ mb: 2, borderBottom: '1px solid #ddd', pb: 1 }}
                  >
                    Comments & History
                  </Typography>
                  <AuditTrailDisplay auditTrail={auditTrail} />
                  {showCommentInput ? (
                    <CommentInput 
                      onSubmit={handleCommentSubmit}
                      onCancel={() => setShowCommentInput(false)}
                      disabled={false}
                    />
                  ) : (
                    <Button
                      variant="outlined"
                      onClick={() => setShowCommentInput(true)}
                      sx={{ mt: 2 }}
                    >
                      Add Comment
                    </Button>
                  )}
                </Box>
              </Grid>
            )}

            {/* Submit error — shown when save/edit API call fails */}
            <Grid item xs={12}>
              <Collapse in={Boolean(errors.submitError)}>
                <Alert
                  severity="error"
                  onClose={() => setErrors(prev => ({ ...prev, submitError: '' }))}
                  sx={{ mb: 1 }}
                >
                  {errors.submitError}
                </Alert>
              </Collapse>
            </Grid>

            {/* Action Buttons */}
            <Grid
              item
              xs={12}
              sx={{ display: 'flex', gap: 2, mt: 3, alignItems: 'center' }}
            >
              <Button
                color="primary"
                variant="contained"
                disabled={!previewButtonEnabled || isSubmitting}
                onClick={handlePreviewChange}
                startIcon={isSubmitting ? <CircularProgress size={16} color="inherit" /> : null}
              >
                {isSubmitting
                  ? 'Saving...'
                  : changeDetailsState.id ? 'Save Changes' : 'Create Bridge Column'
                }
              </Button>
              <Button
                color="secondary"
                variant="outlined"
                onClick={handleCancelChange}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              {isSubmitting && (
                <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                  Applying change to the grid…
                </Typography>
              )}
            </Grid>
          </Grid>
        </Paper>
      );
    }
    console.error('No active project', activeProjectInfo);
    return null;
};

export default ChangeDetails;